import React from 'react';
import { AiPodCanvasNode, AiPodConnection, AiPodConditionType } from '../../types/ai-pod-canvas';
import { Button } from '../ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../ui/Field';

export interface AiPodAgentTypeOption {
  id: string;
  code: string;
  name: string;
  shortDescription?: string | null;
}

interface AiPodInspectorProps {
  selectedNode?: AiPodCanvasNode;
  selectedConnection?: AiPodConnection;
  agentTypes: AiPodAgentTypeOption[];
  onUpdateNode: (nodeId: string, updates: Partial<AiPodCanvasNode>) => void;
  onDeleteNode: (nodeId: string) => void;
  onUpdateConnection: (connectionId: string, updates: Partial<AiPodConnection>) => void;
  onDeleteConnection: (connectionId: string) => void;
  onAddRouterBranch?: (nodeId: string) => void;
  onClose: () => void;
  onSave: () => void;
}

const CONDITION_TYPE_OPTIONS: AiPodConditionType[] = ['custom', 'intent', 'outcome', 'user-response', 'fallback'];

// Same trigger types as the tenant workflow editor.
const TRIGGER_TYPE_OPTIONS = [
  { value: 'manual', label: 'Manual / chat' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'inbound-call', label: 'Inbound call' },
  { value: 'outbound-call', label: 'Outbound call' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

/** Injects the currently-assigned agent type into the select list even if it's since
 * been deactivated/removed, so a pod referencing it still displays sensibly. */
const agentTypesForSelect = (node: AiPodCanvasNode, agentTypes: AiPodAgentTypeOption[]): AiPodAgentTypeOption[] => {
  const agentTypeId = String(node.data?.agentTypeId ?? '').trim();
  if (!agentTypeId || agentTypes.some((a) => a.id === agentTypeId)) return agentTypes;
  return [{ id: agentTypeId, code: 'assigned', name: node.title || node.name || 'Assigned agent type' }, ...agentTypes];
};

export const AiPodInspector: React.FC<AiPodInspectorProps> = ({
  selectedNode,
  selectedConnection,
  agentTypes,
  onUpdateNode,
  onDeleteNode,
  onUpdateConnection,
  onDeleteConnection,
  onAddRouterBranch,
  onClose,
  onSave,
}) => {
  const isOpen = Boolean(selectedNode || selectedConnection);

  const updateNodeData = (node: AiPodCanvasNode, updates: Record<string, unknown>) => {
    onUpdateNode(node.id, { data: { ...node.data, ...updates } });
  };

  const renderNodeEditor = (node: AiPodCanvasNode) => {
    const nodeClass = node.nodeClass ?? node.data?.nodeClass ?? 'conversation';

    return (
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Title</span>
          <TextField tone="default"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={node.title ?? node.name}
            onChange={(e) => onUpdateNode(node.id, { title: e.target.value, name: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Summary</span>
          <TextArea tone="default"
            className="w-full rounded-md border px-3 py-2 text-sm"
            rows={2}
            value={node.summary ?? ''}
            onChange={(e) => onUpdateNode(node.id, { summary: e.target.value })}
          />
        </label>

        {nodeClass === 'trigger' && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">Trigger type</span>
            <SelectField tone="default"
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={String(node.data?.triggerType ?? 'manual')}
              onChange={(e) => updateNodeData(node, { triggerType: e.target.value })}
            >
              {TRIGGER_TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </SelectField>
            <span className="mt-1 block text-[11px] text-gray-500">
              How a subscriber's workflow starts. Its entry agent is created for this channel.
            </span>
          </label>
        )}

        {(nodeClass === 'trigger' || nodeClass === 'conversation' || nodeClass === 'action') && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">
              {nodeClass === 'trigger' ? 'Entry Agent Type' : 'Agent Type'}
            </span>
            <SelectField tone="default"
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={String(node.data?.agentTypeId ?? '')}
              onChange={(e) => {
                const selected = agentTypesForSelect(node, agentTypes).find((a) => a.id === e.target.value);
                onUpdateNode(node.id, {
                  data: { ...node.data, agentTypeId: e.target.value },
                  ...(selected?.name ? { title: selected.name, name: selected.name } : {}),
                });
              }}
            >
              <option value="">Select an agent type</option>
              {agentTypesForSelect(node, agentTypes).map((agentType) => (
                <option key={agentType.id} value={agentType.id}>
                  {agentType.name}
                </option>
              ))}
            </SelectField>
          </label>
        )}

        {nodeClass === 'human-handoff' && (
          <>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Queue ID</span>
              <TextField tone="default"
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={String(node.data?.queueId ?? '')}
                onChange={(e) => updateNodeData(node, { queueId: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Team ID</span>
              <TextField tone="default"
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={String(node.data?.teamId ?? '')}
                onChange={(e) => updateNodeData(node, { teamId: e.target.value })}
              />
            </label>
          </>
        )}

        {nodeClass === 'router' && (
          <>
            <div className="space-y-2">
              <span className="block text-xs font-medium text-gray-600">Branches</span>
              {node.outputs.map((output) => (
                <div key={output.id} className="flex items-center gap-2">
                  <TextField tone="default"
                    className="flex-1 rounded-md border px-3 py-2 text-sm"
                    value={output.name}
                    onChange={(e) => {
                      const nextOutputs = node.outputs.map((o) => (o.id === output.id ? { ...o, name: e.target.value } : o));
                      onUpdateNode(node.id, { outputs: nextOutputs });
                    }}
                  />
                  {node.outputs.length > 2 && (
                    <Button tone="danger-link"
                      type="button"
                      className="text-xs"
                      onClick={() => onUpdateNode(node.id, { outputs: node.outputs.filter((o) => o.id !== output.id) })}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button tone="custom"
                type="button"
                className="text-xs text-blue-600 hover:text-blue-800"
                onClick={() => onAddRouterBranch?.(node.id)}
              >
                + Add branch
              </Button>
            </div>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Condition expression</span>
              <TextArea tone="default"
                className="w-full rounded-md border px-3 py-2 text-sm font-mono"
                rows={2}
                value={String(node.data?.expression ?? '')}
                onChange={(e) => updateNodeData(node, { expression: e.target.value })}
                placeholder="e.g., outcome === 'qualified'"
              />
            </label>
          </>
        )}

        {nodeClass === 'end' && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">Result</span>
            <SelectField tone="default"
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={String(node.data?.result ?? 'success')}
              onChange={(e) => updateNodeData(node, { result: e.target.value })}
            >
              <option value="success">Success</option>
              <option value="failure">Failure</option>
            </SelectField>
          </label>
        )}

        <Button tone="custom"
          type="button"
          className="text-sm text-red-600 hover:text-red-800"
          onClick={() => onDeleteNode(node.id)}
        >
          Delete component
        </Button>
      </div>
    );
  };

  const renderConnectionEditor = (connection: AiPodConnection) => (
    <div className="space-y-4">
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-600">Label</span>
        <TextField tone="default"
          className="w-full rounded-md border px-3 py-2 text-sm"
          value={connection.label ?? ''}
          onChange={(e) => onUpdateConnection(connection.id, { label: e.target.value })}
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-600">Condition Type</span>
        <SelectField tone="default"
          className="w-full rounded-md border px-3 py-2 text-sm"
          value={connection.conditionType ?? 'custom'}
          onChange={(e) => onUpdateConnection(connection.id, { conditionType: e.target.value as AiPodConditionType })}
        >
          {CONDITION_TYPE_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </SelectField>
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-600">Priority</span>
        <TextField tone="default"
          type="number"
          className="w-full rounded-md border px-3 py-2 text-sm"
          value={connection.priority ?? ''}
          onChange={(e) => onUpdateConnection(connection.id, { priority: e.target.value ? Number(e.target.value) : undefined })}
        />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
         
          checked={!!connection.isLoopback}
          onChange={(e) => onUpdateConnection(connection.id, { isLoopback: e.target.checked })}
        />
        Mark as loopback transition
      </label>
      <Button tone="custom"
        type="button"
        className="text-sm text-red-600 hover:text-red-800"
        onClick={() => onDeleteConnection(connection.id)}
      >
        Delete transition
      </Button>
    </div>
  );

  return (
    <div
      className={`absolute right-0 top-0 z-40 h-full w-96 transform overflow-y-auto bg-white shadow-xl transition-transform ${isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">
            {selectedNode ? 'Edit Component' : selectedConnection ? 'Edit Transition' : ''}
          </h3>
          <Button tone="custom" className="text-gray-400 hover:text-gray-600" onClick={onClose}>✕</Button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {selectedNode ? renderNodeEditor(selectedNode) : null}
          {selectedConnection ? renderConnectionEditor(selectedConnection) : null}
        </div>
        <div className="border-t border-gray-100 px-4 py-3">
          <Button tone="blue"
            type="button"
            className="w-full rounded-md px-3 py-2 text-sm"
            onClick={onSave}
          >
            Save
          </Button>
          <p className="mt-1 text-center text-[11px] text-gray-400">Save persists the pod's composition graph.</p>
        </div>
      </div>
    </div>
  );
};

export default AiPodInspector;
